import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add, AccountTree, Edit, Delete } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import {
  BOMComponent,
  createBOMLine,
  updateBOMLine,
  deleteBOMLine,
} from '../../../store/slices/masterDataSlice';

const BOMManager: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { boms, items, bomsLoading } = useSelector((state: RootState) => state.masterData);

  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLine, setEditingLine] = useState<BOMComponent | null>(null);
  const [form, setForm] = useState<{ childItemId: string; quantity: number; scrapPercent: number }>({
    childItemId: '',
    quantity: 1,
    scrapPercent: 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedBOM = boms.find((b) => b.itemId === selectedItemId);
  const components = selectedBOM?.components ?? [];

  const bomParentIds = new Set(boms.map((b) => b.itemId));
  const parentItems = items.filter(
    (item) =>
      bomParentIds.has(item.id) ||
      item.type === 'finished_good' ||
      item.type === 'semi_finished' ||
      item.type === 'sub_assembly'
  );

  const childItemOptions = items.filter(
    (item) => item.id !== selectedItemId && item.status === 'active'
  );

  const openAddDialog = () => {
    setEditingLine(null);
    setForm({ childItemId: '', quantity: 1, scrapPercent: 0 });
    setError(null);
    setDialogOpen(true);
  };

  const openEditDialog = (line: BOMComponent) => {
    setEditingLine(line);
    setForm({ childItemId: line.childItemId, quantity: line.quantity, scrapPercent: line.scrapPercent });
    setError(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.childItemId || !form.quantity) return;
    setSaving(true);
    setError(null);
    const action = editingLine
      ? await dispatch(updateBOMLine({ id: editingLine.id, quantity: form.quantity, scrapPercent: form.scrapPercent }))
      : await dispatch(
          createBOMLine({
            parentItemId: selectedItemId,
            childItemId: form.childItemId,
            quantity: form.quantity,
            unitOfMeasure: items.find((i) => i.id === form.childItemId)?.uom || 'PC',
            scrapPercent: form.scrapPercent,
          })
        );
    setSaving(false);
    if ((action as any).meta?.requestStatus === 'rejected') {
      setError(String((action as any).payload ?? 'Save failed'));
      return;
    }
    setDialogOpen(false);
  };

  const handleRemove = async (line: BOMComponent) => {
    if (!window.confirm(`Remove ${line.childItemCode} from this BOM?`)) return;
    setError(null);
    const action = await dispatch(deleteBOMLine(line.id));
    if ((action as any).meta?.requestStatus === 'rejected') {
      setError(String((action as any).payload ?? 'Remove failed'));
    }
  };

  const getTotalQuantityWithScrap = (c: BOMComponent) => {
    return (c.quantity * (1 + c.scrapPercent / 100)).toFixed(3);
  };

  return (
    <Box>
      {/* BOM Selector */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <AccountTree sx={{ color: 'primary.main' }} />
          <Typography variant="h6" sx={{ mr: 1 }}>
            Bill of Materials Manager
          </Typography>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel>Select Parent Item</InputLabel>
            <Select
              value={selectedItemId}
              label="Select Parent Item"
              onChange={(e) => setSelectedItemId(e.target.value)}
            >
              <MenuItem value="">Select an item...</MenuItem>
              {parentItems.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.code} — {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedItemId && (
            <Button variant="contained" startIcon={<Add />} onClick={openAddDialog} size="small">
              Add Component
            </Button>
          )}
          {bomsLoading && <CircularProgress size={20} />}
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* BOM Details */}
      {selectedItemId && (
        <Paper sx={{ p: 2 }}>
          {selectedBOM && (
            <Box display="flex" gap={2} mb={2} alignItems="center" flexWrap="wrap">
              <Typography variant="h6">
                BOM: {selectedBOM.itemCode} — {selectedBOM.itemName}
              </Typography>
              <Chip label={`v${selectedBOM.version}`} size="small" variant="outlined" />
              <Chip
                label={selectedBOM.status.toUpperCase()}
                color={selectedBOM.status === 'active' ? 'success' : 'default'}
                size="small"
              />
              <Typography variant="caption" color="textSecondary">
                Effective from: {new Date(selectedBOM.effectiveFrom).toLocaleDateString()}
              </Typography>
            </Box>
          )}

          {!selectedBOM && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No BOM exists for this item yet. Add a component to create one — changes save immediately.
            </Alert>
          )}

          {components.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Component Code</TableCell>
                    <TableCell>Component Name</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell>UOM</TableCell>
                    <TableCell align="right">Scrap%</TableCell>
                    <TableCell align="right">Qty w/ Scrap</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {components.map((comp, index) => (
                    <TableRow key={comp.id}>
                      <TableCell>
                        <Typography variant="caption" color="textSecondary">
                          {index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {comp.childItemCode}
                        </Typography>
                      </TableCell>
                      <TableCell>{comp.childItemName}</TableCell>
                      <TableCell align="right">{comp.quantity}</TableCell>
                      <TableCell>{comp.uom}</TableCell>
                      <TableCell align="right">{comp.scrapPercent.toFixed(1)}%</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="info.main">
                          {getTotalQuantityWithScrap(comp)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Button size="small" startIcon={<Edit />} onClick={() => openEditDialog(comp)}>
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => handleRemove(comp)}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {!selectedItemId && (
        <Alert severity="info">
          Select a finished good, sub-assembly or semi-finished item above to view or edit its Bill of Materials.
        </Alert>
      )}

      {/* Add / Edit Component Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingLine ? `Edit Component — ${editingLine.childItemCode}` : 'Add BOM Component'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            {!editingLine && (
              <FormControl size="small" fullWidth>
                <InputLabel>Component Item *</InputLabel>
                <Select
                  value={form.childItemId}
                  label="Component Item *"
                  onChange={(e) => setForm({ ...form, childItemId: e.target.value })}
                >
                  <MenuItem value="">Select item...</MenuItem>
                  {childItemOptions.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.code} — {item.name} ({item.uom})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Box display="flex" gap={2}>
              <TextField
                label="Quantity *"
                type="number"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })}
                size="small"
                sx={{ flex: 1 }}
                inputProps={{ min: 0.001, step: 0.001 }}
              />
              <TextField
                label="Scrap %"
                type="number"
                value={form.scrapPercent}
                onChange={(e) => setForm({ ...form, scrapPercent: Number(e.target.value) })}
                size="small"
                sx={{ flex: 1 }}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
              />
            </Box>
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || (!editingLine && !form.childItemId) || !form.quantity}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {editingLine ? 'Save Changes' : 'Add Component'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BOMManager;
